import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatMoney } from '@/utils/format';
import { PublicOrderPayCard, type PaymentOffer } from './PublicOrderPayCard';

/**
 * The customer's copy of a phone order, opened from the text they were sent.
 * No login, no dashboard chrome: one page that reads like a receipt on a
 * phone screen, served by the token in the link and nothing else.
 */

interface PublicOrder {
  business: string;
  orderNumber: string;
  status: string;
  fulfillment: string;
  requestedTime?: string;
  readyInMinutes: number;
  customerName?: string;
  placedAt: string;
  currency: string;
  items: { name: string; quantity: number; modifiers: string[]; lineTotalCents: number; note?: string }[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  location: { name: string; address: string; phone?: string };
  /** Present only when the store takes card payment on this page. */
  payment?: PaymentOffer;
}

const STATUS_LINE: Record<string, string> = {
  SUBMITTING: 'Being sent to the store',
  CONFIRMED: 'Confirmed',
  POS_SYNCED: 'Confirmed',
  POS_PENDING: 'Confirmed',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  POS_SYNC_FAILED: 'Please call the store to confirm'
};

export function PublicOrderPage() {
  const { token = '' } = useParams();
  const [order, setOrder] = useState<PublicOrder | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/public/orders/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (res.status === 404) return 'missing' as const;
        if (!res.ok) throw new Error(String(res.status));
        return (await res.json()) as PublicOrder;
      })
      .then((result) => {
        if (cancelled) return;
        if (result === 'missing') setState('missing');
        else {
          setOrder(result);
          setState('ready');
        }
      })
      .catch(() => !cancelled && setState('error'));
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state === 'loading') return <Shell>Loading your order…</Shell>;
  if (state === 'missing') return <Shell>This link is not valid. Check the text you received or call the store.</Shell>;
  if (state === 'error' || !order) return <Shell>Something went wrong loading the order. Try again in a moment.</Shell>;

  const placed = new Date(order.placedAt);
  const ready = new Date(placed.getTime() + order.readyInMinutes * 60_000);
  const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const money = (cents: number) => formatMoney(cents, order.currency);

  return (
    <Shell>
      <header className="mb-4">
        <p className="text-small text-slate">{order.business}</p>
        <h1 className="text-xl font-semibold text-ink">Order #{order.orderNumber}</h1>
        <p className="mt-1 text-small text-jade">{STATUS_LINE[order.status] ?? order.status}</p>
      </header>

      <section className="mb-4 rounded-lg border border-rule bg-white p-4">
        <p className="text-small text-ink">
          {order.fulfillment === 'DELIVERY' ? 'Delivery' : order.fulfillment === 'DINE_IN' ? 'Dine in' : 'Pickup'}
          {order.customerName ? ` for ${order.customerName}` : ''}
        </p>
        <p className="text-small text-slate">
          {order.requestedTime && order.requestedTime.toUpperCase() !== 'ASAP'
            ? `Requested for ${order.requestedTime}`
            : `Placed ${time(placed)} · ready around ${time(ready)}`}
        </p>
      </section>

      <section className="mb-4 rounded-lg border border-rule bg-white">
        <ul className="divide-y divide-rule">
          {order.items.map((line, i) => (
            <li key={i} className="flex items-start justify-between gap-3 px-4 py-3">
              <div>
                <p className="text-small text-ink">
                  {line.quantity} × {line.name}
                </p>
                {line.modifiers.length ? <p className="text-xs text-slate">{line.modifiers.join(', ')}</p> : null}
                {line.note ? <p className="text-xs text-slate">“{line.note}”</p> : null}
              </div>
              <p className="text-small text-ink">{money(line.lineTotalCents)}</p>
            </li>
          ))}
        </ul>
        <dl className="space-y-1 border-t border-rule px-4 py-3 text-small">
          <Row label="Subtotal" value={money(order.subtotalCents)} />
          <Row label="Tax" value={money(order.taxCents)} />
          <Row label="Total" value={money(order.totalCents)} strong />
        </dl>
      </section>

      {order.payment?.enabled ? (
        <PublicOrderPayCard
          token={token}
          offer={order.payment}
          onPaid={(paid) => setOrder((current) => (current ? { ...current, payment: paid } : current))}
        />
      ) : null}

      <section className="rounded-lg border border-rule bg-white p-4 text-small">
        <p className="font-semibold text-ink">{order.location.name}</p>
        <p className="text-slate">{order.location.address}</p>
        {order.location.phone ? (
          <a className="mt-2 inline-block text-jade underline" href={`tel:${order.location.phone}`}>
            Call the store
          </a>
        ) : null}
      </section>
    </Shell>
  );
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between ${strong ? 'font-semibold text-ink' : 'text-slate'}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-paper px-4 py-8">
      <div className="mx-auto max-w-md text-small text-slate">{children}</div>
    </main>
  );
}
